'use client'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { Box, IconButton } from '@/CommonComponents'
import { COLORS } from '@/lib/constants/colors'
import { useState, useEffect, useRef } from 'react'
import { useTexts } from '@/contexts/LanguageContext'

function MarkdownContent({ content, placeholder }: { content: string; placeholder: string }) {
  const isEmpty = !content.trim()

  if (isEmpty) {
    return (
      <div
        style={{
          fontSize: '0.875rem',
          color: '#666',
          fontStyle: 'italic',
          minHeight: '20px',
        }}
      >
        {placeholder}
      </div>
    )
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        p: ({ children, ...props }) => (
          <p style={{ fontSize: '0.875rem' }} {...props}>
            {children}
          </p>
        ),
        span: ({ children, style, ...props }) => (
          <span style={{ margin: 0, fontSize: '0.875rem', ...style }} {...props}>
            {children}
          </span>
        ),
        ol: ({ children, ...props }) => (
          <ol style={{ fontSize: '0.875rem' }} {...props}>
            {children}
          </ol>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

function EditIcon({
  isHovered,
  onEditClick,
  isContentEmpty,
}: {
  isHovered: boolean
  onEditClick: () => void
  isContentEmpty: boolean
}) {
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 8,
        right: 8,
        opacity: isHovered || isContentEmpty ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      <IconButton onClick={onEditClick} icon="edit" size="small" color="primary" />
    </Box>
  )
}

function ExpandButton({
  isOverflowing,
  isExpanded,
  onToggle,
}: {
  isOverflowing: boolean
  isExpanded: boolean
  onToggle: () => void
}) {
  const { COMMON } = useTexts()
  const { SHOW_LESS, SHOW_MORE } = COMMON
  if (!isOverflowing) return null

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mt: 1 }}>
      <button
        onClick={onToggle}
        style={{
          border: 'none',
          background: 'none',
          color: COLORS.PRIMARY.MAIN,
          cursor: 'pointer',
          padding: '8px 0',
        }}
      >
        {isExpanded ? SHOW_LESS : SHOW_MORE}
      </button>
    </Box>
  )
}

export default function MarkdownViewer({
  content,
  onChange,
  onEditClick,
  placeholder,
}: {
  content: string
  onChange?: (newContent: string) => void
  onEditClick: () => void
  placeholder?: string
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const { DEFAULT_PLACEHOLDER } = useTexts()
  const contentRef = useRef<HTMLDivElement>(null)
  const MAX_HEIGHT = 120

  useEffect(() => {
    if (contentRef.current) {
      const markdownContent = contentRef.current.querySelector('.markdown-content')
      if (markdownContent) {
        const height = markdownContent.getBoundingClientRect().height
        setContentHeight(height)
        const hasOverflow = height > MAX_HEIGHT
        setIsOverflowing(hasOverflow)
      }
    }
  }, [content])

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => {
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        setIsHovered(false)
      }}
    >
      <div
        ref={contentRef}
        style={{
          maxHeight: isExpanded ? `${contentHeight}px` : `${MAX_HEIGHT}px`,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out',
        }}
      >
        <div className="markdown-content">
          <MarkdownContent content={content} placeholder={placeholder || DEFAULT_PLACEHOLDER} />
        </div>
      </div>

      {onChange && <EditIcon isHovered={isHovered} onEditClick={onEditClick} isContentEmpty={!content.trim()} />}

      <ExpandButton
        isOverflowing={isOverflowing}
        isExpanded={isExpanded}
        onToggle={() => {
          setIsExpanded(!isExpanded)
        }}
      />
    </div>
  )
}
